// src/components/ContactUs.js

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/App.css'; // Import the consolidated App.css
import emailjs from 'emailjs-com';

function ContactUs() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ submitted: false, error: false, loading: false });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic form validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({ submitted: false, error: true, loading: false });
      return;
    }

    setFormStatus({ submitted: false, error: false, loading: true });

    const serviceID = 'your_service_id'; // Replace with your EmailJS service ID
    const templateID = 'your_template_id'; // Replace with your EmailJS template ID
    const userID = 'your_user_id'; // Replace with your EmailJS user ID

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
    };

    emailjs.send(serviceID, templateID, templateParams, userID)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setFormStatus({ submitted: true, error: false, loading: false });
        setFormData({ name: '', email: '', message: '' });
      }, (err) => {
        console.error('FAILED...', err);
        setFormStatus({ submitted: false, error: true, loading: false });
      });
  };

  return (
    <main className="contact-page container py-5">
      {/* Helmet for Meta Tags */}
      <Helmet>
        <title>Contact Us | WizeAssist</title>
        <meta
          name="description"
          content="Get in touch with WizeAssist to learn more about our AI-driven sports training solutions, ask questions, or request a demo."
        />
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Contact Us | WizeAssist" />
        <meta
          property="og:description"
          content="Get in touch with WizeAssist to learn more about our AI-driven sports training solutions, ask questions, or request a demo."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.wizeassist.com/contact" />
        <meta property="og:image" content="https://www.wizeassist.com/assets/images/og-image-contact.jpg" /> {/* Replace with actual image */}

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | WizeAssist" />
        <meta
          name="twitter:description"
          content="Get in touch with WizeAssist to learn more about our AI-driven sports training solutions, ask questions, or request a demo."
        />
        <meta name="twitter:image" content="https://www.wizeassist.com/assets/images/twitter-image-contact.jpg" /> {/* Replace with actual image */}

        {/* Structured Data for Contact Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Us",
            "description": "Get in touch with WizeAssist to learn more about our AI-driven sports training solutions, ask questions, or request a demo.",
            "url": "https://www.wizeassist.com/contact",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "email": "support@wize-assist.com", // Replace with your contact email
              "areaServed": "IL", // Israel's country code
              "availableLanguage": ["English", "Hebrew"] // Add languages as needed
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1234 Sport Ave",
              "addressLocality": "Tel Aviv",
              "addressRegion": "TLV",
              "postalCode": "123456",
              "addressCountry": "IL"
            }
          })}
        </script>
      </Helmet>

      <h1 className="contact-page-title text-center mb-5">Get in Touch</h1>

      <div className="row">
        {/* Contact Form */}
        <section className="col-md-6 contact-form-section mb-4 mb-md-0">
          <h2 className="mb-4">We'd Love to Hear From You</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group mb-3">
              <label htmlFor="name">
                Name<span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={`form-control ${formStatus.error && !formData.name ? 'input-error' : ''}`}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="email">
                Email<span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className={`form-control ${formStatus.error && !formData.email ? 'input-error' : ''}`}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="message">
                Message<span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder="Type your message here..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                className={`form-control ${formStatus.error && !formData.message ? 'input-error' : ''}`}
              ></textarea>
            </div>

            {formStatus.error && (
              <p className="form-error text-danger">Please fill in all the required fields.</p>
            )}
            {formStatus.submitted && (
              <p className="form-success text-success">
                Thank you, {formData.name}! We’ll get back to you soon.
              </p>
            )}

            <button type="submit" className="btn btn-primary w-100" disabled={formStatus.loading}>
              {formStatus.loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </section>

        {/* Contact Information */}
        <section className="col-md-6 contact-info-section">
          <h2 className="mb-4">Contact Information</h2>
          <p>
            If you have any questions or need further information, feel free to reach out to us:
          </p>
          <ul className="contact-info-list list-unstyled">
            <li className="mb-3 d-flex align-items-center">
              <i className="fas fa-envelope me-3 text-teal"></i>
              <span>
                <strong>Email:</strong>{' '}
                <a href="mailto:support@wize-assist.com" className="text-decoration-none">
                  support@wize-assist.com
                </a>
              </span>
            </li>
            <li className="mb-3 d-flex align-items-center">
              <i className="fas fa-map-marker-alt me-3 text-teal"></i>
              <span>
                <strong>Headquarters:</strong> Tel Aviv, Israel
              </span>
            </li>
          </ul>

          {/* Google Maps Embed */}
          <div className="contact-map mt-4">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3023.944779331688!2d34.78176881567544!3d32.08529998092378!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151d4eaf76d4f4bf%3A0x9e1b45e3c5e6a2c!2sTel%20Aviv%20University!5e0!3m2!1sen!2sil!4v1616176133456!5m2!1sen!2sil"
              width="100%"
              height="250"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              title="WizeAssist Headquarters Location"
            ></iframe>
          </div>
        </section>
      </div>
    </main>
  );
}

export default ContactUs;
